import { useEffect, useState } from 'react';
import type { ObservableRef, UseIntersectionObserverOptions, UseIntersectionObserverResult } from './types';

const useIntersectionObserver = (
    observableRef: ObservableRef,
    { threshold = 0, root = null, rootMargin = '0%', once = false }: UseIntersectionObserverOptions = {}
): UseIntersectionObserverResult => {
    const [resultEntry, setResultEntry] = useState<UseIntersectionObserverResult>(null);

    const stringifyThreshold = JSON.stringify(threshold);
    const frozen = Boolean(once && resultEntry && resultEntry.isIntersecting);

    const updateEntry: IntersectionObserverCallback = ([entry]) => setResultEntry(entry);

    useEffect(() => {
        const observableElement = observableRef.current;

        if (frozen || !observableElement) {
            return;
        }

        const observerParams = { threshold: JSON.parse(stringifyThreshold), root, rootMargin };
        const observer = new IntersectionObserver(updateEntry, observerParams);

        observer.observe(observableElement);

        return () => observer.disconnect();
    }, [
        observableRef,
        // since threshold can be an array,
        // but useEffect uses `Object.is` for its comparison algorithm: https://react.dev/reference/react/useEffect#parameters,
        // we need to convert it to be a primitive value
        // to skip unnecessary IntersectionObserver instance re-creations
        stringifyThreshold,
        root,
        rootMargin,
        frozen,
    ]);

    return resultEntry;
};

export default useIntersectionObserver;
